import { InfoCircleOutlined } from '@ant-design/icons';
import { Table, Tag, Tooltip, Typography } from 'antd';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { createSearchParams, Link, Outlet, useSearchParams } from 'react-router-dom';

import useAPIQuery from '../../api/useAPIQuery';
import { withRouteChange } from '../../services/APM';
import { GroupCollection } from '../../types';
import { buildPermissionsMap, getFrontendManualTranslations } from '../../utils';

const AccessProfiles = () => {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();

  const permissions = useAPIQuery(
    [
      '/permissions/',
      {
        params: {
          codename__in: [
            'view_all_vacancies',
            'create_and_edit_vacancies',
            'access_talent_pool',
            'access_configurations',
            'access_reports',
            'view_marked_request_job_postings',
            'view_all_request_job_postings',
            'create_and_edit_request_job_postings',
            'can_approve_marked_request_job_postings',
            'can_approve_all_request_job_postings',
          ].join(','),
        },
      },
    ],
    {
      select(data) {
        return buildPermissionsMap(data);
      },
    }
  );

  const groups = useAPIQuery(
    [
      '/groups/',
      {
        params: {
          page: parseInt(searchParams.get('page') || '1', 10),
          page_size: parseInt(searchParams.get('pageSize') || '10', 10),
        },
      },
    ],
    {
      keepPreviousData: true,
    }
  );

  const config = useAPIQuery('/configs/tenant-configurations/');

  return (
    <>
      <Helmet>
        <title>
          {t('Perfis | Configurações')} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <Outlet />
      <Table
        rowKey="id"
        dataSource={groups.data?.results}
        loading={groups.isFetching || permissions.isFetching}
        pagination={{
          showTotal: (total) => {
            return t(`Total: {{total}} perfis`, { total });
          },
          total: groups.data?.count,
          onChange: (page, pageSize) => {
            searchParams.set('page', page.toString());
            searchParams.set('pageSize', pageSize.toString());
            setSearchParams(searchParams);
          },
          current: parseInt(searchParams.get('page') || '1', 10),
          pageSize: parseInt(searchParams.get('pageSize') || '10', 10),
        }}
      >
        <Table.Column
          title={t('Perfil de acesso')}
          render={(record: GroupCollection) =>
            record.name === 'Padrão' ? (
              <Typography.Text>
                {getFrontendManualTranslations(
                  record.name,
                  config.data?.t007_enableChangeLanguage || false
                )}{' '}
                <Tooltip
                  title={t(
                    'Este perfil é aplicado automaticamente a novos usuários do sistema. Se precisar de permissões diferentes, crie um novo perfil com as configurações desejadas e atribua ao usuário.'
                  )}
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </Typography.Text>
            ) : (
              <Link
                to={{
                  pathname: `${record.id}/change`,
                  search: createSearchParams(searchParams).toString(),
                }}
              >
                {record.name}
              </Link>
            )
          }
        />
        <Table.Column
          title={t('Permissões')}
          render={(record: GroupCollection) => (
            <>
              {permissions.data &&
              record.permissions?.includes(permissions.data.view_all_vacancies) ? (
                <Tag>
                  <Trans>Visualizar todas as vagas</Trans>
                </Tag>
              ) : (
                <Tag>
                  <Trans>Visualizar apenas vagas marcadas</Trans>
                </Tag>
              )}
              {permissions.data &&
                record.permissions?.includes(permissions.data.create_and_edit_vacancies) && (
                  <Tag>
                    <Trans>Criar e editar vagas</Trans>
                  </Tag>
                )}
              {permissions.data &&
                record.permissions?.includes(permissions.data.access_talent_pool) && (
                  <Tag>
                    <Trans>Acesso ao Banco de Talentos</Trans>
                  </Tag>
                )}
              {permissions.data &&
                record.permissions?.includes(permissions.data.access_configurations) && (
                  <Tag>
                    <Trans>Acesso às Configurações</Trans>
                  </Tag>
                )}
              {permissions.data &&
                record.permissions?.includes(permissions.data.access_reports) && (
                  <Tag>
                    <Trans>Acesso aos Relatórios</Trans>
                  </Tag>
                )}
              {permissions.data &&
                record.permissions?.includes(permissions.data.view_marked_request_job_postings) && (
                  <Tag>
                    <Trans>Visualizar apenas as requisições marcadas</Trans>
                  </Tag>
                )}
              {permissions.data &&
                record.permissions?.includes(permissions.data.view_all_request_job_postings) && (
                  <Tag>
                    <Trans>Visualizar todas as requisições</Trans>
                  </Tag>
                )}
              {permissions.data &&
                !record.permissions.includes(permissions.data.view_marked_request_job_postings) &&
                permissions.data &&
                !record.permissions?.includes(permissions.data.view_all_request_job_postings) && (
                  <Tag>
                    <Trans>Sem acesso a requisições</Trans>
                  </Tag>
                )}
              {permissions.data &&
                record.permissions?.includes(
                  permissions.data.create_and_edit_request_job_postings
                ) && (
                  <Tag>
                    <Trans>Criação e Edição de requisições</Trans>
                  </Tag>
                )}
              {permissions.data &&
                record.permissions?.includes(
                  permissions.data.can_approve_marked_request_job_postings
                ) && (
                  <Tag>
                    <Trans>Aprovar apenas requisições marcadas</Trans>
                  </Tag>
                )}
              {permissions.data &&
                record.permissions?.includes(
                  permissions.data.can_approve_all_request_job_postings
                ) && (
                  <Tag>
                    <Trans>Aprovar todas as requisições</Trans>
                  </Tag>
                )}
            </>
          )}
        />
      </Table>
    </>
  );
};

export default withRouteChange()(AccessProfiles);
